import {
    Box,
    Checkbox,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    TextFieldProps,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import React from 'react';
import { FormattedMessage, MessageDescriptor, useIntl } from 'react-intl';
import { useDeviceType } from '../../hooks/useDeviceType';
import { ToolbarProps, ZoomLevel } from './types';

const ZOOM_LEVEL_TRANSLATIONS: Record<ZoomLevel, MessageDescriptor['id']> = {
    [ZoomLevel.DAYS]: 'calendar.field.zoom.days',
    [ZoomLevel.MONTHS]: 'calendar.field.zoom.months',
};

const Toolbar: React.FC<ToolbarProps> = ({
    zoom,
    startDate,
    endDate,
    includeLongLasting,
    onZoomChange,
    onStartChange,
    onEndChange,
    onIncludeLongLastingChange,
    dateFormat,
}) => {
    const intl = useIntl();
    const isMobile: boolean = useDeviceType().isMobile;
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: isMobile ? 'column' : 'row', gap: 2, my: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <DatePicker
                    label={intl.formatMessage({ id: 'calendar.field.scheduledStart' })}
                    value={startDate}
                    onChange={(d) => d && onStartChange(d)}
                    shouldDisableDate={(d) => !!d && d.getTime() > endDate.getTime()}
                    format={dateFormat}
                    slotProps={{ textField: { size: 'small', required: true } as TextFieldProps }}
                />
                <DatePicker
                    label={intl.formatMessage({ id: 'calendar.field.scheduledEnd' })}
                    value={endDate}
                    onChange={(d) => d && onEndChange(d)}
                    shouldDisableDate={(d) => !!d && d.getTime() < startDate.getTime()}
                    format={dateFormat}
                    slotProps={{ textField: { size: 'small', required: true } as TextFieldProps }}
                />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>{intl.formatMessage({ id: 'calendar.field.zoom' })}</InputLabel>
                    <Select
                        value={zoom}
                        label={intl.formatMessage({ id: 'calendar.field.zoom' })}
                        onChange={(e) => onZoomChange(e.target.value as ZoomLevel)}
                    >
                        {Object.values(ZoomLevel)
                            .filter((v): v is ZoomLevel => typeof v !== 'string')
                            .map((level) => (
                                <MenuItem key={level} value={level}>
                                    <FormattedMessage id={ZOOM_LEVEL_TRANSLATIONS[level]} />
                                </MenuItem>
                            ))}
                    </Select>
                </FormControl>
                <FormControlLabel
                    control={
                        <Checkbox
                            value={includeLongLasting}
                            onChange={(e) => onIncludeLongLastingChange(e.target.checked)}
                        />
                    }
                    label={intl.formatMessage({ id: 'calendar.field.zoom.includeLongLasting' })}
                />
            </Box>
        </Box>
    );
};

export { Toolbar };
